import { useMemo, type ReactNode } from 'react';
import {
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  type SxProps,
  type Theme,
} from '@mui/material';
import type { LookupState, Option } from '../types';

type SeasonTeamMultiSelectProps = {
  lookups: LookupState;
  value: string[];
  onChange: (teamIds: string[]) => void;
  label?: string;
  allLabel?: string;
  teams?: Option[];
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  sx?: SxProps<Theme>;
  renderSelected?: (selected: string[]) => ReactNode;
};

export function SeasonTeamMultiSelect({
  lookups,
  value,
  onChange,
  label = 'Team',
  allLabel = 'All Teams',
  teams = lookups.teams,
  size = 'medium',
  fullWidth = true,
  disabled = false,
  multiple = true,
  sx,
  renderSelected,
}: SeasonTeamMultiSelectProps) {
  const sortedTeams = useMemo(() => [...teams].sort((a, b) => a.label.localeCompare(b.label)), [teams]);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const nextValue = event.target.value;
    onChange(typeof nextValue === 'string' ? nextValue.split(',') : nextValue);
  };

  const renderValue = (selected: string[]) => {
    if (selected.length === 0) return allLabel;
    if (selected.length === 1) return teams.find((team) => team.value === selected[0])?.label || selected[0];
    return `${selected.length} teams selected`;
  };

  const menuItems = sortedTeams.map((team) => (
      <MenuItem key={team.value} value={team.value}>
        {multiple && <Checkbox checked={value.includes(team.value)} />}
        {team.label}{team.season_label ? ` · ${team.season_label}` : ''}
      </MenuItem>
    ));

  return (
    <FormControl fullWidth={fullWidth} size={size} disabled={disabled} sx={sx}>
      <InputLabel>{label}</InputLabel>
      {multiple ? (
        <Select
          multiple
          label={label}
          value={value}
          onChange={handleChange}
          renderValue={renderSelected || renderValue}
        >
          {menuItems}
        </Select>
      ) : (
        <Select
          label={label}
          value={value[0] || ''}
          onChange={(event) => onChange(event.target.value ? [event.target.value] : [])}
          renderValue={(selected) => teams.find((team) => team.value === selected)?.label || selected}
        >
          {menuItems}
        </Select>
      )}
    </FormControl>
  );
}
